/**
 * AmbientSoundSystem — Contextual ambient audio for atmosphere.
 *
 * Plays environmental sounds based on proximity to hazards (water drips near
 * water tiles, lava crackles near lava) and random tension sounds (creaks,
 * wind gusts, Vorath whispers at higher levels).
 *
 * The system is designed with placeholder audio keys. When MP3 files are
 * provided in the `sound/` directory, BootScene loads them and this system
 * plays them. If an audio key is not loaded, playback silently fails via
 * try/catch (standard project pattern).
 */

import {
  AUDIO_AMBIENT_WATER_DRIP,
  AUDIO_AMBIENT_LAVA_CRACKLE,
  AUDIO_AMBIENT_VORATH_WHISPER,
  AUDIO_AMBIENT_CREAK_1,
  AUDIO_AMBIENT_CREAK_2,
  AUDIO_AMBIENT_WIND,
  AMBIENT_PROXIMITY_RADIUS,
  AMBIENT_WATER_INTERVAL_MIN,
  AMBIENT_WATER_INTERVAL_MAX,
  AMBIENT_LAVA_INTERVAL_MIN,
  AMBIENT_LAVA_INTERVAL_MAX,
  AMBIENT_VORATH_INTERVAL_MIN,
  AMBIENT_VORATH_INTERVAL_MAX,
  AMBIENT_VORATH_MIN_LEVEL,
  AMBIENT_CREAK_INTERVAL_MIN,
  AMBIENT_CREAK_INTERVAL_MAX,
  AMBIENT_WIND_INTERVAL_MIN,
  AMBIENT_WIND_INTERVAL_MAX,
  AMBIENT_BASE_VOLUME,
  TILE_SIZE,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/**
 * @typedef {Object} AmbientSoundConfig
 * @property {Phaser.Scene} scene     - The GameScene instance
 * @property {number}       level     - Current maze level
 */

export default class AmbientSoundSystem {
  /**
   * @param {AmbientSoundConfig} config
   */
  constructor({ scene, level }) {
    /** @type {Phaser.Scene} */
    this.scene = scene;
    /** @type {number} */
    this.level = level;

    /** @type {boolean} Whether the system is actively scheduling sounds. */
    this._active = false;

    /** @type {Phaser.Time.TimerEvent|null} */
    this._waterTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._lavaTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._creakTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._windTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._vorathTimer = null;

    /** @type {boolean} Whether the player is near water tiles. */
    this._nearWater = false;
    /** @type {boolean} Whether the player is near lava tiles. */
    this._nearLava = false;
  }

  /**
   * Start ambient sound scheduling.
   * Call after maze is built and player is placed.
   */
  start() {
    this._active = true;

    // Start random creaks (always active)
    this._scheduleCreak();

    // Start wind gusts (always active)
    this._scheduleWind();

    // Start Vorath whispers (level 5+)
    if (this.level >= AMBIENT_VORATH_MIN_LEVEL) {
      this._scheduleVorath();
    }

    // Proximity-based sounds (water, lava) are triggered by updatePlayerPosition
  }

  /**
   * Update the system with the player's current grid position.
   * Checks proximity to water/lava hazards and starts/stops proximity timers.
   *
   * @param {number} col - Player grid column
   * @param {number} row - Player grid row
   * @param {number[][]} mazeGrid - The maze grid (CellType values)
   * @param {Object} hazardCells - Object with water/lava cell Sets or null
   * @param {Set<string>|null} hazardCells.water - Set of "col,row" strings for water cells
   * @param {Set<string>|null} hazardCells.lava  - Set of "col,row" strings for lava cells
   */
  updatePlayerPosition(col, row, hazardCells) {
    if (!this._active) return;

    const nearWater = hazardCells.water
      ? this._isNearHazard(col, row, hazardCells.water)
      : false;
    const nearLava = hazardCells.lava
      ? this._isNearHazard(col, row, hazardCells.lava)
      : false;

    // Water proximity changed
    if (nearWater && !this._nearWater) {
      this._nearWater = true;
      this._scheduleWater();
    } else if (!nearWater && this._nearWater) {
      this._nearWater = false;
      this._cancelTimer('_waterTimer');
    }

    // Lava proximity changed
    if (nearLava && !this._nearLava) {
      this._nearLava = true;
      this._scheduleLava();
    } else if (!nearLava && this._nearLava) {
      this._nearLava = false;
      this._cancelTimer('_lavaTimer');
    }
  }

  /**
   * Pause all ambient sound timers.
   */
  pause() {
    this._active = false;
    if (this._waterTimer) this._waterTimer.paused = true;
    if (this._lavaTimer) this._lavaTimer.paused = true;
    if (this._creakTimer) this._creakTimer.paused = true;
    if (this._windTimer) this._windTimer.paused = true;
    if (this._vorathTimer) this._vorathTimer.paused = true;
  }

  /**
   * Resume all ambient sound timers.
   */
  resume() {
    this._active = true;
    if (this._waterTimer) this._waterTimer.paused = false;
    if (this._lavaTimer) this._lavaTimer.paused = false;
    if (this._creakTimer) this._creakTimer.paused = false;
    if (this._windTimer) this._windTimer.paused = false;
    if (this._vorathTimer) this._vorathTimer.paused = false;
  }

  /**
   * Stop all ambient sounds and destroy timers.
   */
  stop() {
    this._active = false;
    this._cancelTimer('_waterTimer');
    this._cancelTimer('_lavaTimer');
    this._cancelTimer('_creakTimer');
    this._cancelTimer('_windTimer');
    this._cancelTimer('_vorathTimer');
    this._nearWater = false;
    this._nearLava = false;
  }

  /**
   * Full cleanup — stop and release all references.
   */
  destroy() {
    this.stop();
    this.scene = null;
  }

  // ---------------------------------------------------------------
  // Private — scheduling
  // ---------------------------------------------------------------

  /** @private Schedule a water drip sound at a random interval. */
  _scheduleWater() {
    this._cancelTimer('_waterTimer');
    const delay = this._randomBetween(AMBIENT_WATER_INTERVAL_MIN, AMBIENT_WATER_INTERVAL_MAX);
    this._waterTimer = this.scene.time.delayedCall(delay, () => {
      this._playAmbient(AUDIO_AMBIENT_WATER_DRIP);
      if (this._nearWater && this._active) {
        this._scheduleWater();
      }
    });
  }

  /** @private Schedule a lava crackle sound at a random interval. */
  _scheduleLava() {
    this._cancelTimer('_lavaTimer');
    const delay = this._randomBetween(AMBIENT_LAVA_INTERVAL_MIN, AMBIENT_LAVA_INTERVAL_MAX);
    this._lavaTimer = this.scene.time.delayedCall(delay, () => {
      this._playAmbient(AUDIO_AMBIENT_LAVA_CRACKLE);
      if (this._nearLava && this._active) {
        this._scheduleLava();
      }
    });
  }

  /** @private Schedule a random creak sound. */
  _scheduleCreak() {
    this._cancelTimer('_creakTimer');
    const delay = this._randomBetween(AMBIENT_CREAK_INTERVAL_MIN, AMBIENT_CREAK_INTERVAL_MAX);
    this._creakTimer = this.scene.time.delayedCall(delay, () => {
      const key = Math.random() < 0.5 ? AUDIO_AMBIENT_CREAK_1 : AUDIO_AMBIENT_CREAK_2;
      this._playAmbient(key);
      if (this._active) {
        this._scheduleCreak();
      }
    });
  }

  /** @private Schedule a wind gust sound. */
  _scheduleWind() {
    this._cancelTimer('_windTimer');
    const delay = this._randomBetween(AMBIENT_WIND_INTERVAL_MIN, AMBIENT_WIND_INTERVAL_MAX);
    this._windTimer = this.scene.time.delayedCall(delay, () => {
      this._playAmbient(AUDIO_AMBIENT_WIND);
      if (this._active) {
        this._scheduleWind();
      }
    });
  }

  /** @private Schedule a Vorath whisper sound. */
  _scheduleVorath() {
    this._cancelTimer('_vorathTimer');
    const delay = this._randomBetween(AMBIENT_VORATH_INTERVAL_MIN, AMBIENT_VORATH_INTERVAL_MAX);
    this._vorathTimer = this.scene.time.delayedCall(delay, () => {
      this._playAmbient(AUDIO_AMBIENT_VORATH_WHISPER, 0.2);
      if (this._active) {
        this._scheduleVorath();
      }
    });
  }

  // ---------------------------------------------------------------
  // Private — helpers
  // ---------------------------------------------------------------

  /**
   * Play an ambient sound at the configured base volume, scaled by SFX settings.
   * @private
   * @param {string} audioKey
   * @param {number} [volumeOverride] - Override base volume (default AMBIENT_BASE_VOLUME)
   */
  _playAmbient(audioKey, volumeOverride) {
    if (!this.scene || !this.scene.sound) return;
    const vol = getSfxVol(volumeOverride ?? AMBIENT_BASE_VOLUME);
    try {
      this.scene.sound.play(audioKey, { volume: vol });
    } catch (_) { /* audio fallback — file may not exist yet */ }
  }

  /**
   * Check if the player is within AMBIENT_PROXIMITY_RADIUS tiles of any
   * hazard cell in the given set.
   * @private
   * @param {number} playerCol
   * @param {number} playerRow
   * @param {Set<string>} hazardSet - Set of "col,row" strings
   * @returns {boolean}
   */
  _isNearHazard(playerCol, playerRow, hazardSet) {
    const radius = AMBIENT_PROXIMITY_RADIUS;
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        const key = `${playerCol + dc},${playerRow + dr}`;
        if (hazardSet.has(key)) return true;
      }
    }
    return false;
  }

  /**
   * Cancel and remove a timer by property name.
   * @private
   * @param {string} timerProp
   */
  _cancelTimer(timerProp) {
    if (this[timerProp]) {
      this[timerProp].destroy();
      this[timerProp] = null;
    }
  }

  /**
   * @private
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  _randomBetween(min, max) {
    return min + Math.random() * (max - min);
  }
}
